/**
 * useReportVersion — fires a single POST to /client-reports on app launch.
 *
 * DeviceId strategy: uses expo-secure-store to persist a UUID generated on first launch.
 * This satisfies NFR-4.4 (stable across restarts, opaque, no PII) without relying on
 * platform-specific vendor IDs that have stricter privacy permissions on iOS 14+.
 *
 * Platform gate: returns immediately on Platform.OS === 'web' (spec FR-4.1).
 * Fire-and-forget: all errors are swallowed — failures NEVER block app launch (NFR-4.1).
 */
import Constants from 'expo-constants';
import { randomUUID } from 'expo-crypto';
import * as SecureStore from 'expo-secure-store';
import * as Updates from 'expo-updates';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { reportVersion } from './api';

const DEVICE_ID_KEY = 'version_reporter_device_id';

async function getOrCreateDeviceId(): Promise<string> {
  const stored = await SecureStore.getItemAsync(DEVICE_ID_KEY);
  if (stored) {
    return stored;
  }
  const id = randomUUID();
  await SecureStore.setItemAsync(DEVICE_ID_KEY, id);
  return id;
}

export function useReportVersion(): void {
  const reported = useRef(false);

  useEffect(() => {
    // Platform gate: web is a no-op per spec FR-4.1
    if (Platform.OS === 'web') {
      return;
    }

    // Idempotency guard: only fire once per mount lifecycle
    if (reported.current) {
      return;
    }
    reported.current = true;

    void (async () => {
      try {
        const deviceId = await getOrCreateDeviceId();

        await reportVersion({
          easBuildId: null,
          updateGroupId: Updates.updateId ?? null,
          channel: Updates.channel ?? null,
          runtimeVersion: Updates.runtimeVersion ?? 'unknown',
          platform: Platform.OS,
          appVersion: Constants.expoConfig?.version ?? 'unknown',
          deviceId,
        });
      } catch {
        // Silently swallow all errors — failures must NOT block app launch (NFR-4.1)
      }
    })();
  }, []);
}
